import { OnlineBookingType, UnauthedMaintenancePlanDetailsViewModel } from '@breezy/shared'
import { faScrewdriverWrench } from '@fortawesome/pro-regular-svg-icons'
import classNames from 'classnames'
import React, { useCallback, useMemo, useState } from 'react'
import { SmartAccountExperienceCompanyInfoBox } from '../../components/AccountExperienceCompanyInfoBox/SmartAccountExperienceCompanyInfoBox'
import { SmartAccountExperienceCompanyInfoSection } from '../../components/AccountExperienceCompanyInfoBox/SmartAccountExperienceCompanyInfoSection'
import { FullScreenLoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner'
import { trpc } from '../../hooks/trpc'
import { useIsLargeScreen, useIsMobile, useIsSmallScreen } from '../../hooks/useIsMobile'
import { UnauthPricebookSettingsProvider } from '../../hooks/usePricebookSettings'
import { HeroIconWithTitleAndMessage } from '../../ui/HeroIconWithTitleAndMessage/HeroIconWithTitleAndMessage'
import { OnsitePageSection } from '../../ui/OnsitePage/OnsitePageSection'
import { useOnsitePageDimensions } from '../../ui/OnsitePage/onsitePageUtils'
import { OnlineBookingFormSkeleton, OnlineBookingSubmittedPage } from './AccountExperienceOnlineBookingPage'
import { BookMaintenanceForm } from './BookMaintenanceForm'
import { WithOnlineBookingContexts } from './onlineBookingUtils'

export const BookMaintenancePage = React.memo(({ maintenancePlanVisitGuid }: { maintenancePlanVisitGuid: string }) => {
  const { data, isFetching } = trpc.maintenancePlans['unauth:maintenance-plans:get-details'].useQuery(
    { maintenancePlanVisitGuid },
    {
      cacheTime: 1000 * 60 * 5, // Cache for 5 minutes
      staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
      refetchOnWindowFocus: false, // Prevent refetch on window focus
    },
  )

  const { data: jobLead, isFetching: isJobLeadFetching } = trpc.jobLeads[
    'unauth:job-leads:get-by-maintenance-plan-visit-guid'
  ].useQuery(
    { maintenancePlanVisitGuid },
    {
      refetchOnWindowFocus: false,
      cacheTime: 1000 * 60 * 5,
      staleTime: 1000 * 60 * 5,
    },
  )

  if (isFetching || isJobLeadFetching) {
    return FullScreenLoadingSpinner
  }
  if (!data) {
    return <div>No account found</div>
  }
  const { companyGuid } = data
  if (!companyGuid) {
    return <div>No company found</div>
  }

  return <BookMaintenanceInner companyGuid={companyGuid} visit={data} jobLead={jobLead} />
})

BookMaintenancePage.displayName = 'BookMaintenancePage'

const BookMaintenanceInner = React.memo(
  ({
    companyGuid,
    visit,
    jobLead,
  }: {
    companyGuid: string
    visit: UnauthedMaintenancePlanDetailsViewModel
    jobLead?: { jobLeadGuid: string } | undefined | null
  }) => {
    const isMobile = useIsMobile()
    const isSmallScreen = useIsSmallScreen()
    const isLargeScreen = useIsLargeScreen()
    const pageDimensions = useOnsitePageDimensions()

    const initialStep = useMemo(() => {
      if (jobLead) {
        return 'submitted-request'
      }
      if (visit.job) {
        return 'submitted-instant-booking'
      }
      return 'pre-submit'
    }, [jobLead, visit.job])

    const [step, setStep] = useState<'pre-submit' | 'submitted-request' | 'submitted-instant-booking'>(initialStep)

    const paddingClassName = useMemo(() => {
      if (isMobile) {
        return 'py-10'
      }
      if (isSmallScreen) {
        return 'py-16'
      }
      return 'p-16'
    }, [isMobile, isSmallScreen])

    // biome-ignore lint/correctness/useExhaustiveDependencies: setStep is a state setter and is stable
    const onSubmitted = useCallback(
      (bookingType: OnlineBookingType) => {
        setStep(bookingType === 'REQUEST' ? 'submitted-request' : 'submitted-instant-booking')
      },
      [setStep],
    )

    return (
      <UnauthPricebookSettingsProvider companyGuid={companyGuid}>
        <div className={classNames('flex flex-row items-start justify-center', paddingClassName)}>
          {isLargeScreen && <SmartAccountExperienceCompanyInfoBox companyGuid={companyGuid} />}
          <div
            style={pageDimensions.style}
            // The estimates always have this padding on the top and bottom because the estimate sections do it that
            // way. It's accounted for everywhere, but here it makes the top of the first section not align with the
            // company info box.
            className={isMobile ? 'my-[-16px]' : 'my-[-24px]'}
          >
            <OnsitePageSection padding="large-mobile">
              {step === 'pre-submit' && (
                <HeroIconWithTitleAndMessage
                  title={`Request Maintenance`}
                  message="Please fill out the form below. Providing detailed information will help us deliver the best possible service."
                  faIcon={faScrewdriverWrench}
                  color="gray"
                />
              )}

              {step === 'submitted-request' || step === 'submitted-instant-booking' ? (
                <OnlineBookingSubmittedPage
                  companyGuid={companyGuid}
                  bookingType={step === 'submitted-request' ? 'REQUEST' : 'INSTANT'}
                />
              ) : null}
            </OnsitePageSection>
            {step === 'pre-submit' && (
              <WithOnlineBookingContexts companyGuid={companyGuid} loadingComponent={<OnlineBookingFormSkeleton />}>
                <BookMaintenanceForm companyGuid={companyGuid} onSubmit={onSubmitted} visit={visit} />
              </WithOnlineBookingContexts>
            )}
            {!isLargeScreen && <SmartAccountExperienceCompanyInfoSection companyGuid={companyGuid} />}
          </div>
        </div>
      </UnauthPricebookSettingsProvider>
    )
  },
)
